import type { MoneyDTO } from '../types.js';
import { amountMentions } from './schedule.js';
import { dateMentions } from './dates.js';

/** How a customer says they paid. Free text is normalised to one of these; anything else stays `other`. */
export const claimMethods = ['ach', 'wire', 'check', 'card', 'bank_transfer', 'other'] as const;
export type ClaimMethod = typeof claimMethods[number];
/** What a vendor notice says is missing before it will accept an invoice. */
export const complianceMissingKinds = ['po', 'w9', 'vendor_form', 'portal_registration', 'other'] as const;
export type ComplianceMissing = typeof complianceMissingKinds[number];

export interface ClaimDetails { on: string | null; amount: MoneyDTO | null; method: ClaimMethod | null; reference: string | null }

const methodPatterns: ReadonlyArray<readonly [ClaimMethod, RegExp]> = [
  ['ach', /\bach\b|automated clearing/i],
  ['wire', /\bwire(?: transfer)?\b/i],
  ['check', /\bche(?:ck|que)\b/i],
  ['card', /\b(?:credit|debit)\s+card\b|\bcard payment\b/i],
  ['bank_transfer', /\bbank transfer\b|\btransfer(?:red)?\b/i],
];
const methodLabels: Record<ClaimMethod, string> = {
  ach: 'ACH', wire: 'wire', check: 'check', card: 'card', bank_transfer: 'bank transfer', other: 'other',
};
export const claimMethodLabel = (method: string) => methodLabels[method as ClaimMethod] ?? method.replaceAll('_', ' ');

const complianceLabels: Record<ComplianceMissing, string> = {
  po: 'PO', w9: 'W-9', vendor_form: 'vendor form', portal_registration: 'portal registration',
  other: 'vendor requirement',
};
export const complianceMissingLabel = (kind: string) => complianceLabels[kind as ComplianceMissing] ?? 'vendor requirement';

const compliancePatterns: ReadonlyArray<readonly [ComplianceMissing, RegExp]> = [
  ['w9', /\bw[\s-]?9\b/i],
  ['portal_registration', /\bportal registration\b|\b(?:supplier|vendor)\s+portal\b|\bregister\s+(?:in|on|with)\b[^.\n]{0,40}\bportal\b/i],
  ['po', /\bpurchase order\b|\bpo number\b|\bpo\s*#|\bpo\b(?!\s*box\b)/i],
  ['vendor_form', /\bvendor (?:form|registration|packet|onboarding|setup|master)\b|\bsupplier (?:form|registration|packet|onboarding|setup|master)\b/i],
  ['other', /\bvendor compliance\b|\bcompliance (?:form|document|requirement)\b/i],
];

/** The method a reply names, normalised; unknown wording stays `other`. */
export function claimMethod(text: string): ClaimMethod | null {
  const found = methodPatterns.find(([, pattern]) => pattern.test(text));
  return found?.[0] ?? null;
}

/** True when the reply text actually says the method the model read. */
export function methodGrounded(method: string, text: string): boolean {
  const pattern = methodPatterns.find(([name]) => name === method)?.[1];
  return pattern ? pattern.test(text) : text.toLowerCase().includes(method.toLowerCase());
}

/** True when the reply text actually names the missing document the model read. */
export function missingGrounded(kind: string, text: string): boolean {
  const pattern = compliancePatterns.find(([name]) => name === kind)?.[1];
  return pattern ? pattern.test(text) : true;
}

function claimClauses(text: string) {
  return text.split(/(?<=[.!?])\s+|\n+/)
    .filter(part => /\b(?:paid|payment|remit(?:ted|tance)?|ach|wire|che(?:ck|que)|transfer|deposit)\b/i.test(part));
}

/**
 * The payment a reply says it made. Amounts and dates come from the reply, never the ledger; when the reply names
 * more than one amount or date, the field stays empty rather than picking one.
 */
export function claimDetails(text: string, receivedOn: string, currency: string | null): ClaimDetails {
  const clauses = claimClauses(text); const scope = clauses.length ? clauses.join('\n') : text;
  const amounts = new Map<string, MoneyDTO>();
  for (const mention of amountMentions(scope, currency)) {
    if (mention.money) amounts.set(`${mention.money.currency}:${mention.money.minor}`, mention.money);
  }
  const dates = new Set(dateMentions(scope, receivedOn).map(mention => mention.date).filter((date): date is string => !!date));
  const reference = /\b(?:ref(?:erence)?|confirmation|transaction|trace)\s*(?:#|no\.?|number|id)?\s*(?:is\s+)?[:#-]?\s*([A-Z0-9][A-Z0-9-]*\d[A-Z0-9-]*|\d[A-Z0-9-]*)/i
    .exec(text)?.[1] ?? null;
  // The schema allows 100 characters; a longer run (a pasted hash, say) is not a usable reference, and letting it through
  // would fail classification and stop the mailbox poll on that one message.
  const usable = reference && reference.length <= 100 ? reference : null;
  return { on: dates.size === 1 ? [...dates][0]! : null, amount: amounts.size === 1 ? [...amounts.values()][0]! : null,
    method: claimMethod(text), reference: usable };
}

/** Strong, literal payment-claim wording. A plain "we will pay" or "we have not paid" never matches. */
export function obviousPaymentClaim(text: string): boolean {
  if (/\b(?:not|never|haven['’]?t|hasn['’]?t|didn['’]?t)\s+(?:yet\s+)?(?:paid|pay|remit)/i.test(text)) return false;
  if (/\b(?:will|plan(?:ning)? to|going to|intend(?:ing)? to)\s+pay\b/i.test(text)) return false;
  return [
    /\b(?:we|i)(?:['’]ve| have)? already (?:paid|remitted|sent (?:the |a )?payment)\b/i,
    /\b(?:remittance|payment) (?:advice|confirmation|proof)\b/i,
    /\bproof of payment\b/i,
    /\bpaid (?:the )?invoice\b/i,
    /\bwe (?:have )?paid (?:invoice|inv)\b/i,
    /\bpayment (?:was|has been) (?:sent|made|remitted)\b/i,
    /\b(?:ach|wire|bank transfer)\b[^.\n]{0,40}\b(?:paid|sent|remitted|payment)\b/i,
  ].some(pattern => pattern.test(text));
}

const documentPattern = /\b(?:purchase order|\bpo\b(?!\s*box\b)|po number|po\s*#|w[\s-]?9|vendor form|vendor (?:registration|packet|onboarding|setup|master)|supplier (?:form|registration|packet|onboarding|setup|master)|portal registration|(?:supplier|vendor)\s+portal|vendor compliance)\b/i;
const documentNoun = String.raw`\b(?:po|purchase order|w[\s-]?9|vendor form|supplier form|vendor registration|supplier registration|portal registration|vendor compliance)`;
const missingPattern = new RegExp([
  String.raw`\binvoices?\b[^.\n]{0,80}\b(?:is\s+|are\s+|has been\s+|have been\s+)?(?:on hold|held|rejected|cannot be processed|can['’]t be processed)\b`,
  String.raw`\b(?:missing|without)\s+(?:a\s+|the\s+|your\s+)?${documentNoun}\b`,
  String.raw`${documentNoun}\b[^.\n]{0,40}\b(?:is\s+|are\s+)?(?:required|missing|not on file|not received|not provided|outstanding)\b`,
  String.raw`\bplease\s+(?:provide|send|submit|complete|upload|register)\b[^.\n]{0,60}\b${documentNoun}\b`,
  String.raw`\b(?:register|registration)\b[^.\n]{0,40}\b(?:supplier|vendor)\s+portal\b`,
].join('|'), 'i');

/** An obvious vendor-compliance notice: a document is named and the mail says it is missing or required. */
export function obviousVendorCompliance(text: string): { missing: ComplianceMissing; detail: string } | null {
  if (!documentPattern.test(text) || !missingPattern.test(text)) return null;
  const clauses = text.split(/(?<=[.!?])\s+|\n+/).filter(part => documentPattern.test(part) && missingPattern.test(part));
  const detail = (clauses[0] ?? text).trim().slice(0, 200);
  const found = compliancePatterns.map(([kind, pattern]) => ({ kind, index: pattern.exec(text)?.index ?? -1 }))
    .filter(entry => entry.index >= 0 && entry.kind !== 'other').sort((a, b) => a.index - b.index)[0]
    ?? compliancePatterns.map(([kind, pattern]) => ({ kind, index: pattern.exec(text)?.index ?? -1 }))
      .filter(entry => entry.index >= 0).sort((a, b) => a.index - b.index)[0];
  return { missing: found?.kind ?? 'other', detail };
}
